import api from 'services/api';
import type { Recurrence } from 'api/recurrence_definitions/endpoints/types';
import { array, bool, enforce, object, optional, runSchema, str } from 'validation';

export interface FirstOccurrenceRequest {
  readonly starttime: string;
  readonly starttime_timezone?: string;
  readonly recurrence?: Recurrence | null;
  readonly scheduling_window?: string;
}

export interface SchedulingWindowPeriod {
  readonly open: string;
  readonly close: string;
}

export interface SchedulingWindowEvaluation {
  readonly valid_at_starttime: boolean;
  readonly available_periods: ReadonlyArray<SchedulingWindowPeriod>;
}

export interface FirstOccurrenceResponse {
  readonly first_occurrence: string;
  readonly scheduling_window?: SchedulingWindowEvaluation;
}

const schedulingWindowPeriodSchema = object<SchedulingWindowPeriod>({
  open: str(),
  close: str(),
});

const schedulingWindowEvaluationSchema = object<SchedulingWindowEvaluation>({
  valid_at_starttime: bool(),
  available_periods: array(schedulingWindowPeriodSchema),
});

// Boundary validation guarantees the *shape* only: first_occurrence is present
// and a string, so the returned type is honest. A missing/null/non-string
// response drifts the field and `enforce` rejects it rather than letting
// `undefined` flow through. Whether the string is a parseable datetime is a
// value concern owned by the consumer, which feeds it into moment() and
// guards isValid().
const firstOccurrenceSchema = object<FirstOccurrenceResponse>({
  first_occurrence: str(),
  scheduling_window: optional(schedulingWindowEvaluationSchema),
});

/**
 * Calculates when the first node instance should be scheduled.
 *
 * - Interval recurrence with no start_time: returns starttime unchanged.
 * - Recurrence with start_time or unit = window_open: returns the next
 *   occurrence after starttime (using starttime − 1s as the anchor).
 * - No recurrence, scheduling_window set: returns starttime if the window is
 *   open, otherwise the next opening after starttime.
 * - When scheduling_window is set, the response also includes window
 *   evaluation at starttime (valid_at_starttime, available_periods).
 */
export async function fetchFirstOccurrence(body: FirstOccurrenceRequest): Promise<FirstOccurrenceResponse> {
  const res = await api.post('/api/recurrence/first-occurrence', body);
  const result = runSchema(firstOccurrenceSchema, res.data, 'fetchFirstOccurrence');
  if (!result.ok) {
    throw new Error('fetchFirstOccurrence: first-occurrence response failed validation');
  }
  return enforce(result.value, result.warnings, ['first_occurrence']);
}
